import { PageEvents } from '@griply/types';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback } from 'react';
import { ResizableContainer } from 'shared/components/ui/resizable-container';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { UpcomingViewOptions } from 'shared/types/upcoming-view-options';
import { targetIsInput } from 'shared/utils/target-is-input';

import { Main } from './components/main';
import { MobileAddTaskButton } from './components/mobile-add-task-button';
import { SidePanel } from './components/side-panel';
import { useSelectedView } from './hooks/use-selected-view';
import * as Styled from './upcoming.style';

const initialSidePanelWidth = 288;

export const Upcoming: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.Upcoming, undefined);

  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();

  const { view, setView } = useSelectedView();

  const toggleSidePanelOpen = useCallback(
    () =>
      updateUserSettings({
        upcoming: { sidePanelOpen: !user?.settings?.upcoming?.sidePanelOpen },
      }),
    [updateUserSettings, user?.settings?.upcoming?.sidePanelOpen],
  );

  const sidePanelOpen =
    view === UpcomingViewOptions.Calendar &&
    !!user?.settings?.upcoming?.sidePanelOpen;
  const setSidePanelWidth = useCallback(
    (width: number) =>
      updateUserSettings({ upcoming: { sidePanelWidth: width } }),
    [updateUserSettings],
  );

  useKeysDown(
    [['KeyA']],
    (e) => {
      if (targetIsInput(e.target) || view !== UpcomingViewOptions.Calendar) {
        return; // Don't open dialog
      }

      toggleSidePanelOpen();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <Main
            sidPanelOpen={sidePanelOpen}
            onToggleSidePanel={toggleSidePanelOpen}
            selectedView={view}
            onChangeView={setView}
          />
        </Styled.MainContent>
      </Styled.Main>

      <Styled.SidePanel $open={sidePanelOpen}>
        <Styled.SidePanelContent $open={sidePanelOpen}>
          <ResizableContainer
            open={sidePanelOpen}
            resizeHandles="w"
            onWidthChange={setSidePanelWidth}
            initialWidth={
              user?.settings?.upcoming?.sidePanelWidth ?? initialSidePanelWidth
            }
            smoothTransition
          >
            {sidePanelOpen && <SidePanel onClose={toggleSidePanelOpen} />}
          </ResizableContainer>
        </Styled.SidePanelContent>
      </Styled.SidePanel>

      <MobileAddTaskButton />
    </Styled.Container>
  );
};
